import { toast } from 'react-toastify'
import {
  useCreateWalletCardTemplateMutation,
  useUpdateWalletCardTemplateMutation,
  useDeleteWalletCardTemplateMutation,
  useCreateWalletCardTemplateDraftMutation,
  useUpdateWalletCardTemplateDraftMutation,
} from '../../../../../services/cellcast.v2'
import {
  CreateWalletCardTemplate,
} from '../../../../../services/cellcast.v2.types'
import { useI18n } from '../../../../Utils/_helpers/useI18n'
import { WalletCardTemplateDetail } from '../types'

/**
 * Custom hook for wallet card template operations.
 * Wraps the RTK Query mutation `useCreateWalletCardTemplateMutation` and provides
 * convenient helper around it (loading state, toast notifications, etc.).
 */
export const useWalletCardTemplate = () => {
  const { t } = useI18n()

  // RTK Query mutations for wallet card template operations
  const [createWalletCardTemplateRTK, { isLoading: isCreating }] =
    useCreateWalletCardTemplateMutation()
  const [updateWalletCardTemplateRTK, { isLoading: isUpdating }] =
    useUpdateWalletCardTemplateMutation()
  const [deleteWalletCardTemplateRTK, { isLoading: isDeleting }] =
    useDeleteWalletCardTemplateMutation()
  const [createWalletCardTemplateDraftRTK, { isLoading: isDraftCreating }] =
    useCreateWalletCardTemplateDraftMutation()
  const [updateWalletCardTemplateDraftRTK, { isLoading: isDraftUpdating }] =
    useUpdateWalletCardTemplateDraftMutation()

  /**
   * Create a new wallet card template.
   * @param payload Data needed to create the template
   * @returns The created template id on success
   */
  const createWalletCardTemplate = async (payload: CreateWalletCardTemplate) => {
    try {
      const result = await createWalletCardTemplateRTK(payload).unwrap()
      toast.success(t('MOBILE_WALLET_CARD.TEMPLATE.CREATE_SUCCESS'))
      if (result.data?.id) {
        return result.data.id
      }
      // Fallback if API does not return id inside `data`
      return undefined
    } catch (error: any) {
      toast.error(error?.data?.message || t('MOBILE_WALLET_CARD.TEMPLATE.CREATE_FAILED'))
      throw error
    }
  }

  // Update existing wallet card template
  const updateWalletCardTemplate = async (payload: WalletCardTemplateDetail) => {
    try {
      const result = await updateWalletCardTemplateRTK(payload).unwrap()
      toast.success(t('MOBILE_WALLET_CARD.TEMPLATE.UPDATE_SUCCESS'))
      return result.data.id
    } catch (error: any) {
      toast.error(error?.data?.message || t('MOBILE_WALLET_CARD.TEMPLATE.UPDATE_FAILED'))
      throw error
    }
  }

  // Delete wallet card template
  const deleteWalletCardTemplate = async (id: string) => {
    try {
      const result = await deleteWalletCardTemplateRTK({ id }).unwrap()
      toast.success(t('MOBILE_WALLET_CARD.TEMPLATE.DELETE_SUCCESS'))
      return result.data
    } catch (error: any) {
      toast.error(error?.data?.message || t('MOBILE_WALLET_CARD.TEMPLATE.DELETE_FAILED'))
      throw error
    }
  }

  // Create draft template
  const createWalletCardTemplateDraft = async (payload: CreateWalletCardTemplate) => {
    try {
      const result = await createWalletCardTemplateDraftRTK(payload).unwrap()
      toast.success(t('MOBILE_WALLET_CARD.TEMPLATE.DRAFT_CREATE_SUCCESS'))
      return result.data
    } catch (error: any) {
      toast.error(error?.data?.message || t('MOBILE_WALLET_CARD.TEMPLATE.DRAFT_CREATE_FAILED'))
      throw error
    }
  }

  // Update draft template
  const updateWalletCardTemplateDraft = async (payload: WalletCardTemplateDetail) => {
    try {
      const result = await updateWalletCardTemplateDraftRTK(payload).unwrap()
      toast.success(t('MOBILE_WALLET_CARD.TEMPLATE.DRAFT_UPDATE_SUCCESS'))
      return result.data
    } catch (error: any) {
      toast.error(error?.data?.message || t('MOBILE_WALLET_CARD.TEMPLATE.DRAFT_UPDATE_FAILED'))
      throw error
    }
  }

  return {
    // mutations
    createWalletCardTemplate,
    updateWalletCardTemplate,
    deleteWalletCardTemplate,
    createWalletCardTemplateDraft,
    updateWalletCardTemplateDraft,
    // loading flags
    isCreating,
    isUpdating,
    isDeleting,
    isDraftCreating,
    isDraftUpdating,
  }
}

export default useWalletCardTemplate;
